import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Flame, Award, TrendingUp } from "lucide-react";

const BADGE_COLORS = {
  bronze: "#CD7F32",
  silver: "#C0C0C0",
  gold: "#FFD60A",
  platinum: "#00E5FF",
  legend: "#AF52DE",
};

/**
 * Streak + badge summary bar for the current user. Feeds off /api/user/streak.
 * Provide `reloadKey` (e.g. an incrementing number) to force refresh after
 * a "mark as watched" action elsewhere on the page.
 */
export default function StreakBar({ reloadKey = 0 }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/user/streak");
        setData(data);
      } catch {
        setData(null);
      }
    })();
  }, [reloadKey]);

  if (!data) return null;
  const earned = data.badges_earned || [];
  const next = data.next_badge;
  const longest = data.longest_streak_weeks || 0;
  const progress = next ? Math.min(100, Math.round((longest / next.weeks) * 100)) : 100;

  return (
    <div className="surface-card p-6 mb-10" data-testid="streak-bar">
      <div className="grid md:grid-cols-3 gap-6">
        <div>
          <div className="label-eyebrow mb-2 flex items-center gap-2 text-[var(--ff-red)]"><Flame size={14} /> ▮ CURRENT STREAK</div>
          <div className="font-display text-5xl leading-none" data-testid="current-streak">
            {data.current_streak_weeks}
            <span className="text-lg text-[var(--ff-text-dim)] font-mono-ui ml-2">wk</span>
          </div>
          <div className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] mt-1">
            Watch any saved show this week to keep it alive.
          </div>
        </div>
        <div>
          <div className="label-eyebrow mb-2 flex items-center gap-2"><TrendingUp size={14} /> ▮ ALL-TIME BEST</div>
          <div className="font-display text-5xl leading-none" data-testid="longest-streak">
            {longest}
            <span className="text-lg text-[var(--ff-text-dim)] font-mono-ui ml-2">wk</span>
          </div>
          <div className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] mt-1">
            Total watches: {data.total_watches}
          </div>
        </div>
        <div>
          <div className="label-eyebrow mb-2 flex items-center gap-2"><Award size={14} /> ▮ BADGES</div>
          {earned.length === 0 ? (
            <div className="text-sm text-[var(--ff-text-dim)]">
              Watch 2 weeks in a row to unlock <span className="text-white">Bronze 🥉</span>.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="badges-earned">
              {earned.map((b) => (
                <span key={b.key}
                      className="px-2.5 py-1 border font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] flex items-center gap-1.5"
                      style={{ borderColor: BADGE_COLORS[b.key], color: BADGE_COLORS[b.key] }}
                      data-testid={`badge-${b.key}`}>
                  <span>{b.emoji}</span> {b.label} · {b.weeks}wk
                </span>
              ))}
            </div>
          )}
          {next && (
            <div className="mt-3">
              <div className="text-[0.65rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)] mb-1">
                Next: {next.emoji} {next.label} ({longest}/{next.weeks} wk)
              </div>
              <div className="h-1.5 bg-[var(--ff-bg)] border border-[var(--ff-border)]">
                <div className="h-full transition-all" style={{ width: `${progress}%`, background: BADGE_COLORS[next.key] }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
